import { useCallback, useEffect, useRef, useState } from "react"

// Time constant for the zoom glide. Time-based rather than per-frame, so it feels the same at
// 60Hz, 120Hz and 144Hz.
const ZOOM_TAU_MS = 80

/**
 * Target-driven exponential glide, so rapid +/- clicks accumulate into one continuous zoom.
 * `min`/`max` clamp the target to the caller's valid scale range.
 */
export const useZoomTween = (initial, min, max) => {
  const [scale, setScale] = useState(initial)
  // Mirrored as state so the zoom buttons can disable on the same click that hits the limit.
  const [target, setTarget] = useState(initial)
  const scaleRef = useRef(initial)
  const targetRef = useRef(initial)
  // The exact in-flight animated value, updated synchronously each frame so a restart continues
  // from where the eye is, not from the render-lagged scale state.
  const currentRef = useRef(initial)
  const rafRef = useRef(null)

  useEffect(() => {
    scaleRef.current = scale
  }, [scale])

  useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    },
    []
  )

  const animateScale = useCallback(
    (next) => {
      targetRef.current = Math.max(min, Math.min(max, next))
      setTarget(targetRef.current)
      // Always cancel any in-flight tween and restart from the exact current position. The old
      // "skip if a frame is already queued" guard could wedge: if that frame was ever throttled or
      // dropped without clearing rafRef (backgrounded tab, globe scrolled out of view), rafRef
      // stayed non-null and every later zoom/reset silently did nothing until remount.
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      let last = performance.now()
      const tick = (now) => {
        // Time-constant easing: the zoom should finish in about the same wall-clock time at any
        // frame rate. Cap dt only to swallow a pathological gap (a backgrounded tab, seconds of
        // stalled rAF). A steady-but-low frame rate must still advance by the real elapsed time,
        // or the easing under-counts each frame and a zoom-out drags out over seconds.
        const dt = Math.min(250, now - last)
        last = now
        const distance = targetRef.current - currentRef.current
        if (Math.abs(distance) < 0.5) {
          currentRef.current = targetRef.current
          setScale(targetRef.current)
          rafRef.current = null
          return
        }
        currentRef.current += distance * (1 - Math.exp(-dt / ZOOM_TAU_MS))
        setScale(currentRef.current)
        rafRef.current = requestAnimationFrame(tick)
      }
      rafRef.current = requestAnimationFrame(tick)
    },
    [min, max]
  )

  return { scale, target, scaleRef, targetRef, animateScale }
}

export default useZoomTween
